import React, { useState, useEffect, useMemo } from 'react';
import './DepositPaymentModal.css';
import mercadoPagoLogo from '../assets/images/Mercado/mercadopago.webp';
import { API_URL } from '../config/api';

const NOMBRE_DE_DIA = {
  monday: 'Lunes',
  tuesday: 'Martes',
  wednesday: 'Miércoles',
  thursday: 'Jueves',
  friday: 'Viernes',
  saturday: 'Sábado',
  sunday: 'Domingo',
};

// Función para formatear horarios del negocio
const formatBusinessSchedule = (schedule) => {
  if (!schedule || typeof schedule !== 'object') {
    return 'Consultar horarios por WhatsApp';
  }

  const diasAbiertos = Object.entries(schedule)
    .filter(([, dia]) => dia?.enabled && dia.ranges?.length > 0)
    .map(([nombre, dia]) => {
      const rangos = dia.ranges.map((r) => `${r.start}-${r.end}`).join(', ');
      return `${NOMBRE_DE_DIA[nombre]}: ${rangos}`;
    });

  return diasAbiertos.length > 0 ? diasAbiertos.join(' • ') : 'Consultar horarios por WhatsApp';
};

/**
 * Los montos se muestran con separador de miles: $12.500, no $12500.
 *
 * A cuatro cifras sin separar hay que contar los dígitos para saber cuánto es, y
 * este número es justo el que decide si alguien reserva o cierra la pestaña.
 */
const PESOS = new Intl.NumberFormat('es-AR', {
  style: 'currency',
  currency: 'ARS',
  maximumFractionDigits: 0,
});
const enPesos = (monto) => PESOS.format(Number(monto) || 0);

/**
 * El dibujito de cada método, aparte de sus datos.
 *
 * Vive acá y no adentro del objeto del método porque ese objeto se serializa: va
 * a `onPaymentSelected`, de ahí al cuerpo de `POST /api/bookings` y termina
 * guardado en `depositInfo` de la reserva. El de Mercado Pago es un elemento de
 * React, y en desarrollo un elemento arrastra `_owner`, que es un FiberNode, que
 * apunta al nodo del DOM, que apunta de vuelta al fiber. `JSON.stringify` de eso
 * tira "Converting circular structure to JSON" y la reserva no se llega a crear.
 *
 * Los otros tres eran emojis —o sea texto— y por eso efectivo y transferencia
 * funcionaban y Mercado Pago no: el único método con un icono de verdad era el
 * único que rompía.
 *
 * El alt va vacío a propósito: el nombre del método está escrito al lado, y un
 * lector de pantalla que lea las dos cosas dice "Mercado Pago Mercado Pago".
 */
const ICONO_DE_METODO = {
  mercadopago: <img src={mercadoPagoLogo} alt="" className="payment-method-logo" />,
  transferencia: '🏦',
  efectivo: '💵',
  uala: '📱',
};

/**
 * Los pasos van en una lista ordenada de verdad, numerada por CSS.
 *
 * Antes el número era un emoji al principio del texto (1️⃣, 2️⃣). Se ve igual y
 * se lee distinto: un lector de pantalla anuncia "teclado con el número uno" en
 * vez de "uno", y no dice cuántos pasos hay ni en cuál va.
 */
const Pasos = ({ children }) => <ol className="payment-steps">{children}</ol>;
const Paso = ({ children }) => <li className="step">{children}</li>;

const Nota = ({ tono = 'aviso', children }) => (
  <p className={`payment-note payment-note--${tono}`}>{children}</p>
);

/**
 * Cómo confirmar el pago, cuando el negocio lo escribió con sus palabras.
 *
 * Va como texto y no como HTML: lo escribe el negocio desde su panel y termina
 * en la pantalla de todos sus clientes.
 */
const ComoConfirmar = ({ texto }) => (
  <div className="confirmation-instructions">
    <strong>Cómo confirmar el pago</strong>
    <p>{texto}</p>
  </div>
);

const Dato = ({ etiqueta, children }) => (
  <div className="bank-detail">
    <span className="bank-detail-label">{etiqueta}</span>
    <span className="bank-detail-value">{children}</span>
  </div>
);

/**
 * Las instrucciones de cada método, en JSX.
 *
 * Eran cinco cadenas de HTML metidas con `dangerouslySetInnerHTML`, y eso traía
 * tres problemas a la vez. Uno: los datos del negocio —dirección, CBU, las
 * instrucciones de confirmación— se interpolaban crudos, así que lo que el
 * negocio escribiera en su panel se ejecutaba en el navegador de sus clientes.
 * Dos: como el HTML no existía hasta el render, sus estilos vivían sueltos en el
 * CSS sin nadie que los reclamara, y algunos se habían escrito inline dentro de
 * la cadena (`style="background: #fff3cd"`) porque desde ahí era más fácil que
 * encontrar la clase. Tres: React no puede recorrer texto, así que nada de esto
 * se podía cambiar sin releer las cinco cadenas enteras.
 */
function InstruccionesDePago({ metodo, monto, bancarios, negocio, servicio }) {
  const instruccionesPropias = servicio.depositConfig?.confirmationInstructions;

  if (metodo.id === 'mercadopago') {
    return (
      <>
        <h4>Pago con Mercado Pago</h4>
        <p>Al continuar te llevamos al pago.</p>
        <Pasos>
          <Paso>Guardamos tu turno y te mandamos a Mercado Pago.</Paso>
          <Paso>Elegís cómo pagar: tarjeta, dinero en cuenta o efectivo.</Paso>
          <Paso>Tu turno se confirma solo cuando el pago entra.</Paso>
        </Pasos>
        <Nota>El link vence en 30 minutos. Si no llegás a pagar, el turno queda pendiente.</Nota>
      </>
    );
  }

  if (metodo.id === 'transferencia') {
    return (
      <>
        <h4>Datos para la transferencia</h4>
        <div className="bank-details">
          <Dato etiqueta="CBU">{bancarios?.cbu || 'No configurado'}</Dato>
          <Dato etiqueta="Alias">{bancarios?.alias || 'No configurado'}</Dato>
          <Dato etiqueta="Titular">
            {bancarios?.accountHolder || negocio?.name || 'No configurado'}
          </Dato>
          <Dato etiqueta="Banco">{bancarios?.bankName || 'No especificado'}</Dato>
          <Dato etiqueta="Monto">{enPesos(monto)}</Dato>
        </div>
        {instruccionesPropias ? (
          <ComoConfirmar texto={instruccionesPropias} />
        ) : (
          <Pasos>
            <Paso>Transferí {enPesos(monto)} a los datos de arriba.</Paso>
            <Paso>Mandá el comprobante por WhatsApp.</Paso>
            <Paso>Esperá la confirmación, hasta una hora.</Paso>
            <Paso>Tu turno queda confirmado.</Paso>
          </Pasos>
        )}
        {!bancarios?.cbu && (
          <Nota tono="error">
            El negocio todavía no cargó sus datos bancarios. Elegí otro método o consultá por
            WhatsApp.
          </Nota>
        )}
      </>
    );
  }

  if (metodo.id === 'efectivo') {
    return (
      <>
        <h4>Pago en el local</h4>
        <div className="location-info">
          <Dato etiqueta="Dirección">
            {negocio?.address || 'Consultar ubicación por WhatsApp'}
          </Dato>
          <Dato etiqueta="Horarios">{formatBusinessSchedule(negocio?.schedule)}</Dato>
          <Dato etiqueta="Monto">{enPesos(monto)}</Dato>
        </div>
        {instruccionesPropias ? (
          <ComoConfirmar texto={instruccionesPropias} />
        ) : (
          <Pasos>
            <Paso>Acercate al local en horario de atención.</Paso>
            <Paso>Entregá {enPesos(monto)} en efectivo.</Paso>
            {/* El código del turno no existe todavía: lo devuelve el backend al
                crear la reserva, o sea después de este diálogo. Antes acá decía
                "Mencioná tu código: {code}" y salía vacío, porque leía un campo
                que en este punto del flujo nunca está. */}
            <Paso>Decí tu nombre y el código que te mostramos al confirmar.</Paso>
            <Paso>Tu turno queda confirmado en el momento.</Paso>
          </Pasos>
        )}
        <Nota>Tenés 24 horas para pagar la seña.</Nota>
      </>
    );
  }

  if (metodo.id === 'uala') {
    return (
      <>
        <h4>Pago con billetera digital</h4>
        {instruccionesPropias ? (
          <ComoConfirmar texto={instruccionesPropias} />
        ) : (
          <Pasos>
            <Paso>Transferí {enPesos(monto)} desde tu billetera al negocio.</Paso>
            <Paso>Mandanos la captura por WhatsApp.</Paso>
            <Paso>Esperá la confirmación.</Paso>
          </Pasos>
        )}
        <Nota>Funciona con Ualá, Modo, Cuenta DNI y otras billeteras.</Nota>
      </>
    );
  }

  return null;
}

function DepositPaymentModal({
  service,
  bookingInfo,
  onPaymentSelected,
  onCancel,
  businessData,
}) {
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [customerConsent, setCustomerConsent] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [bankingData, setBankingData] = useState(null);
  const [error, setError] = useState(null);
  const [closing, setClosing] = useState(false);
  const [integrations, setIntegrations] = useState(null);

  // Cargar datos bancarios e integraciones del negocio al montar el componente
  useEffect(() => {
    const loadBusinessData = async () => {
      try {
        if (!businessData?.id) return;

        // Cargar datos bancarios - endpoint público
        const bankingResponse = await fetch(`${API_URL}/api/business/${businessData.id}/banking-data`);

        if (bankingResponse.ok) {
          const bankingData = await bankingResponse.json();
          setBankingData(bankingData.bankingData);
        }

        // Cargar integraciones del negocio (endpoint público)
        const integrationsResponse = await fetch(`${API_URL}/api/business/${businessData?.id}/integrations`);

        if (integrationsResponse.ok) {
          const integrationsData = await integrationsResponse.json();
          setIntegrations(integrationsData.integrations);
        }
      } catch (error) {
        console.log('Error cargando datos del negocio:', error);
      }
    };

    loadBusinessData();
  }, [businessData?.id]); // Depender solo de businessData.id

  const cerrar = () => {
    setClosing(true);
    setTimeout(() => {
      setClosing(false);
      onCancel();
    }, 250);
  };

  /**
   * Mientras el diálogo está abierto, la página de atrás no se mueve.
   *
   * Es la misma pareja de gestos que hacen los diálogos del panel
   * (AgregarTurno, BloquearHorarios): Escape cierra y el scroll del cuerpo
   * queda trabado. Escape no cierra si se está procesando un pago, porque ahí
   * ya se abrió una ventana de Mercado Pago y cerrar acá deja el turno a medio
   * camino.
   */
  useEffect(() => {
    const alApretar = (e) => {
      if (e.key === 'Escape' && !isProcessing) cerrar();
    };
    document.addEventListener('keydown', alApretar);

    const scrollPrevio = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', alApretar);
      document.body.style.overflow = scrollPrevio;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProcessing]);

  /**
   * Cuánto hay que señar.
   *
   * Se redondea a peso entero porque es lo que después se le manda a Mercado
   * Pago y lo que la persona ve escrito; que la pantalla diga un número y el
   * link de pago cobre otro es la clase de diferencia que termina en un reclamo.
   */
  const montoSenia = useMemo(() => {
    const config = service?.depositConfig || {};
    const cantidad = config.amount ?? 50;
    if ((config.type || 'percentage') === 'percentage') {
      return Math.round(((service?.price || 0) * cantidad) / 100);
    }
    return Math.round(cantidad);
  }, [service]);

  const restante = Math.max(0, Math.round((service?.price || 0) - montoSenia));

  /**
   * Los métodos que el negocio ofrece para esta seña.
   *
   * Mercado Pago se cae de la lista si el negocio no conectó su cuenta: sin
   * cuenta no hay a dónde cobrar, y ofrecerlo para después explicar que no anda
   * es hacerle perder el tiempo a quien está reservando.
   */
  const availablePaymentMethods = useMemo(() => {
    // Sólo datos: nada de lo que hay acá adentro puede ser un elemento de React,
    // porque este objeto termina serializado en la reserva. Ver ICONO_DE_METODO.
    const catalogo = {
      mercadopago: {
        id: 'mercadopago',
        name: 'Mercado Pago',
        description: 'Tarjeta de débito o crédito, al instante',
      },
      transferencia: {
        id: 'transferencia',
        name: 'Transferencia',
        description: 'Con CBU o alias, desde tu banco',
      },
      efectivo: {
        id: 'efectivo',
        name: 'Efectivo',
        description: 'En el local, antes del turno',
      },
      uala: {
        id: 'uala',
        name: 'Ualá / Modo',
        description: 'Billeteras digitales',
      },
    };

    let elegidos = service?.depositConfig?.paymentMethods || [
      'mercadopago',
      'efectivo',
      'transferencia',
    ];

    // Migrar servicios existentes que solo tengan 'efectivo'
    if (elegidos.length === 1 && elegidos[0] === 'efectivo') {
      elegidos = ['mercadopago', 'efectivo', 'transferencia'];
    }

    return elegidos
      .map((id) => catalogo[id])
      .filter((metodo) => {
        if (!metodo) return false;
        if (metodo.id === 'mercadopago' && !integrations?.mercadoPago?.connected) return false;
        return true;
      });
  }, [service, integrations]);

  const handlePaymentMethodSelect = (method) => {
    setSelectedPaymentMethod(method);
    setError(null);
  };

  /**
   * Este diálogo elige el método; no cobra.
   *
   * Acá adentro había dos funciones que pedían el link de pago por su cuenta
   * —`generateMercadoPagoLink` y `generateDigitalWalletLink`— y son la razón
   * por la que la seña no se podía pagar. Cuatro cosas fallaban encadenadas:
   *
   * 1. Pedían el link ANTES de que la reserva existiera, así que mandaban
   *    `bookingId: 'temp_...'`. Mercado Pago guarda ese id como
   *    `external_reference`, que es por donde el webhook encuentra el turno a
   *    confirmar: una preferencia con id temporal no se puede confirmar nunca.
   * 2. Mandaban `bookingInfo.customerName/customerEmail/customerPhone`, que no
   *    existen: la reserva pendiente guarda a la persona en `customer: {}`.
   *    Viajaban los tres `undefined`.
   * 3. Abrían el link con `window.open` DESPUÉS de un `await`, o sea fuera del
   *    gesto del click. El navegador lo bloquea por popup. Ese es el "no pasa
   *    nada" que se veía al apretar Continuar.
   * 4. Y como el resultado de esa llamada decidía si seguir, cuando fallaba
   *    —siempre— se cortaba acá: no se llamaba a `onPaymentSelected`, así que
   *    la reserva no se creaba y el link de verdad no se pedía nunca.
   *
   * Todo eso ya estaba resuelto un nivel más arriba: `handleDepositPaymentSelected`
   * en BusinessPage crea la reserva primero, pide el link con el id real y los
   * datos reales del cliente, y navega con `window.location.href`, que no es un
   * popup y no se bloquea. El trabajo de este diálogo termina acá.
   */
  const handleContinue = async () => {
    if (!selectedPaymentMethod || !customerConsent) return;

    // Lo único que sí conviene revisar antes de crear la reserva: pedir una
    // transferencia a un negocio que no cargó su CBU deja a la persona con un
    // turno tomado y sin a dónde transferir.
    if (selectedPaymentMethod.id === 'transferencia' && !bankingData?.cbu) {
      setError('El negocio todavía no cargó sus datos bancarios. Elegí otro método.');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Se espera al padre a propósito: mientras crea la reserva y pide el link
      // el botón tiene que seguir diciendo "Procesando…". Sin el await, volvía a
      // habilitarse enseguida y se podía apretar dos veces.
      await onPaymentSelected({
        paymentMethod: selectedPaymentMethod,
        depositAmount: montoSenia,
        status: selectedPaymentMethod.id === 'mercadopago' ? 'pending_payment' : 'pending_confirmation',
      });
    } catch (error) {
      console.error('Error al procesar el pago:', error);
      setError('No pudimos seguir con el pago. Probá de nuevo.');
    } finally {
      setIsProcessing(false);
    }
  };

  const formatDate = (date) =>
    new Intl.DateTimeFormat('es-AR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    }).format(date);

  if (!service) return null;

  const porcentaje = service.depositConfig?.type === 'percentage';

  return (
    <div className={`deposit-modal-overlay${closing ? ' closing' : ''}`}>
      <div
        className={`deposit-payment-modal${closing ? ' closing' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="titulo-senia"
      >
        <header className="deposit-modal-header">
          <h2 id="titulo-senia">Seña requerida</h2>
          <button
            className="deposit-modal-close"
            onClick={cerrar}
            aria-label="Cerrar"
            disabled={isProcessing}
          >
            ✕
          </button>
        </header>

        {/* El error queda fuera del cuerpo que scrollea: si apareciera adentro,
            el aviso de que algo falló podría estar fuera de la vista justo
            cuando se acaba de apretar el botón que lo provocó. */}
        {error && (
          <div className="deposit-error" role="alert">
            {error}
          </div>
        )}

        {/* data-lenis-prevent: ver DepositPaymentModal.css */}
        <div className="deposit-modal-body" data-lenis-prevent>
          <section className="deposit-columna deposit-columna--resumen">
            <h3>Tu turno</h3>
            <div className="summary-details">
              <div className="summary-item">
                <span className="label">Servicio</span>
                <span className="value">{service.name}</span>
              </div>
              <div className="summary-item">
                <span className="label">Fecha</span>
                <span className="value">{formatDate(bookingInfo.date)}</span>
              </div>
              <div className="summary-item">
                <span className="label">Hora</span>
                <span className="value">{bookingInfo.time} hs</span>
              </div>
              <div className="summary-item">
                <span className="label">Precio total</span>
                <span className="value">{enPesos(service.price)}</span>
              </div>
            </div>

            <div className="deposit-highlight">
              <span className="deposit-label">Seña a pagar</span>
              <span className="deposit-value">{enPesos(montoSenia)}</span>
              <span className="deposit-type">
                {porcentaje ? `${service.depositConfig?.amount || 50}% del total` : 'Monto fijo'}
              </span>
              <span className="remaining-amount">
                Quedan {enPesos(restante)} para el día del turno
              </span>
            </div>

            <div className="deposit-policy">
              <h4>Política de cancelación</h4>
              <p>
                {service.depositConfig?.cancellationPolicy ||
                  'Flexible: devolución total hasta 24 hs antes.'}
              </p>
            </div>
          </section>

          <section className="deposit-columna deposit-columna--pago">
            <h3>¿Cómo querés pagarla?</h3>
            <div className="payment-methods-grid">
              {availablePaymentMethods.map((method) => (
                <button
                  key={method.id}
                  type="button"
                  className={`payment-method-card ${selectedPaymentMethod?.id === method.id ? 'selected' : ''}`}
                  onClick={() => handlePaymentMethodSelect(method)}
                  disabled={isProcessing}
                  aria-pressed={selectedPaymentMethod?.id === method.id}
                >
                  <span className="payment-method-icon">{ICONO_DE_METODO[method.id]}</span>
                  <span className="payment-method-name">{method.name}</span>
                  <span className="payment-method-description">{method.description}</span>
                </button>
              ))}
            </div>

            {selectedPaymentMethod ? (
              <div className="payment-instructions-section">
                <InstruccionesDePago
                  metodo={selectedPaymentMethod}
                  monto={montoSenia}
                  bancarios={bankingData}
                  negocio={businessData}
                  servicio={service}
                />
              </div>
            ) : (
              <p className="payment-placeholder">
                Elegí un método y acá te decimos cómo seguir.
              </p>
            )}
          </section>
        </div>

        <footer className="deposit-modal-actions">
          <label className="consent-checkbox">
            <input
              type="checkbox"
              checked={customerConsent}
              onChange={(e) => setCustomerConsent(e.target.checked)}
              disabled={isProcessing}
            />
            <span className="consent-text">
              Acepto la política de cancelación y me comprometo a pagar la seña de{' '}
              {enPesos(montoSenia)}.
            </span>
          </label>

          <div className="deposit-modal-botones">
            <button
              type="button"
              className="btn-cancel-deposit"
              onClick={cerrar}
              disabled={isProcessing}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn-continue-deposit"
              onClick={handleContinue}
              disabled={!selectedPaymentMethod || !customerConsent || isProcessing}
            >
              {isProcessing ? 'Procesando…' : 'Continuar'}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default DepositPaymentModal;
